import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSession } from "next-auth/client";
import { t } from "@lingui/macro";
import { includes } from "lodash-es";
import { useRouter } from "next/router";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { PageHead } from "src/molecules/PageHead";
import { Content } from "src/atoms/Content";
import { Modal } from "src/atoms/Modal";
import { Skeleton } from "src/atoms/Skeleton";
import { getLocaleGenderId, setGender } from "src/modules/page";
import { ProductOrder } from "src/organisms/ProductOrder";
import { OutOfStock } from "src/molecules/OutOfStock";
import { ProductBreadcrumbs } from "src/molecules/ProductBreadcrumbs";
import { ProductInfo } from "src/molecules/ProductInfo";
import { ProductMeta } from "src/molecules/ProductMeta";
import { ProductPhotos } from "src/molecules/ProductPhotos";
import { ProductSidebarSkeleton } from "src/molecules/ProductSideBarSkeleton";
import {
  ProductGender,
  ProductVariation,
  PromotionDisplayType,
  PromotionType,
  ShopType,
  SingleProduct,
} from "src/types/graphql.d";
import { SizeGuide } from "src/organisms/SizeGuide";
import { useSegment } from "src/hooks/useSegment";
import { UpdateCartData } from "src/hooks/useUpdateCart";
import { ProductContent, StyledProductView } from "./ProductView.styled";
import { googleTagManager } from "src/tracking/googleTagManager";
import { AppCtx, ViewedProductState } from "src/contexts/app.context";
import { useIncreaseCartItem } from "src/hooks/useIncreaseCartItem";
import Error from "pages/_error";
import currencyMap from "src/utils/currencyMap";
import { useCart } from "src/hooks/useCart";
import AddToCollection from "src/organisms/AddToCollection/AddToCollection";
import ProductViewCarousels from "src/organisms/ProductView/ProductViewCarousels";
import { SEGMENT_ENGAGEMENT_TIMEOUT } from "src/segment";
import { AlgoliaSearchBrand } from "src/types/Algolia";
import AlgoliaBrandInfo from "src/organisms/ProductView/AlgoliaBrandInfo";
import { useLingui } from "@lingui/react";
import NoSSR from "src/atoms/NoSSR";
import { PromoTimer } from "src/molecules/PromoTimer";
import { PromotionalRibbon } from "../PromotionalRibbon";
import { Flash } from "src/icons/Flash";
import { PromotionalBlock } from "src/molecules/PromotionalBlock";
import { usePromotion } from "src/hooks/usePromotion";
import { usePromotionContext } from "src/hooks/usePromotionContext";
import { getCTAttribute } from "src/utils/ct/attributeHelper";
import { LoginRegisterModal } from "src/molecules/LoginRegisterModal";
import {
  removeStorageItem,
  setStorageJSON,
} from "src/utils/helper/localStorage";
import {
  GATED_POPUP_REDIRECT_DATA,
  GATED_PRODUCT_PAGE,
} from "src/constants/gatedItem";
import { useNewIsLoggedIn } from "src/hooks/useIsLoggedIn";
import { ApolloError } from "@apollo/client";
import { getCartInfo } from "src/utils/ct/cart-helper";
import {
  usePageViewedContext,
  usePageViewedDispatch,
} from "src/hooks/context/use-page-viewed-context";
import {
  useShopTypeContext,
  useShopTypeDispatch,
} from "src/hooks/context/use-shop-type-context";
import Cookies from "js-cookie";
import { ImageCarouselContextProvider } from "src/contexts/image-carousel.context";
interface Props {
  slug: string;
  productLoading: boolean;
  productError: ApolloError | string | null;
  product?: SingleProduct;
  brand?: AlgoliaSearchBrand | null;
}

const ProductView: FC<Props> = ({
  slug,
  productLoading,
  productError,
  product,
  brand,
}) => {
  const { i18n } = useLingui();
  const dispatch = useDispatch();
  const router = useRouter();
  const [session] = useSession();
  const { isLoggedIn, isSessionLoading } = useNewIsLoggedIn();
  const { pageViewTracked } = usePageViewedContext();
  const pageViewedDispatch = usePageViewedDispatch();
  const { currentShopType } = useShopTypeContext();
  const shopTypeDispatch = useShopTypeDispatch();

  const algoliaIndex = router.query.algoliaIndex as string;
  const objectID = router.query.objectID as string;
  const queryID = router.query.queryID as string;
  const path = router.asPath;

  const [size, setSize] = useState<ProductVariation | null>();
  const [isOpenSizeGuide, setIsOpenSizeGuide] = useState(false);
  const { data, loading: isCartLoading } = useCart();
  const sizeGuideRef = useRef<HTMLDivElement>(null);
  const timerID = useRef<number>();
  const localeGenderId = useSelector(getLocaleGenderId);
  const [isLoginRegisterModalOpen, setIsLoginRegisterModalOpen] =
    useState(false);
  const [actionSource, setActionSource] = useState<string>("");
  const [addToCartVariationId, setAddToCartVariationId] = useState<string>();

  const { data: promoData } = usePromotion(
    slug,
    PromotionType.Product,
    localeGenderId as ShopType
  );
  const { promotionContextDispatch, promotionContextState } =
    usePromotionContext();

  // variationId contains sku number
  const selectedVariationId = useRef<string>();
  const {
    locale,
    setViewedProductState,
    setAddedProdToCart,
    setPageViewedProperties,
  } = useContext(AppCtx);

  const currency = currencyMap[locale] || "EUR";

  useEffect(() => {
    setPageViewedProperties({
      page_type: "product",
    });
  }, [setPageViewedProperties]);

  useEffect(() => {
    if (currentShopType) {
      shopTypeDispatch({ type: "SET_SHOP_TYPE", payload: currentShopType });
      Cookies.set("ot_shop_type", currentShopType);
    }
  }, [currentShopType, shopTypeDispatch]);

  useEffect(() => {
    if (!promoData || !promoData.promotion) {
      return;
    }

    const currentDate = new Date().getTime();
    const endDate = new Date(
      promoData.promotion.promo_end_date || ""
    ).getTime();

    promotionContextDispatch({
      type: "SET_PROMO",
      payload: {
        promotionDisplayType:
          promoData.promotion.promo_display_type ||
          PromotionDisplayType.Default,
        hasPromotionEnded: currentDate > endDate,
        promotionEndDate: promoData.promotion.promo_end_date || "",
        promotionMessage: promoData.promotion.promo_message || "",
        promotionStartDate: promoData.promotion.promo_start_date || "",
        promotion: promoData.promotion,
      },
    });
  }, [promoData, promotionContextDispatch]);

  useEffect(() => {
    if (productLoading) {
      setViewedProductState(ViewedProductState.LOADING);
    } else if (productError) {
      setViewedProductState(ViewedProductState.ERROR);
    } else if (product) {
      setViewedProductState(ViewedProductState.READY);
    }
  }, [productLoading, product, productError, setViewedProductState]);

  const currentGenderId =
    localeGenderId &&
    includes(product?.product_shop_type || [], localeGenderId) &&
    localeGenderId;

  const availableSizes: ProductVariation[] =
    product?.variations?.filter(
      (variation: ProductVariation) => variation.stock_quantity > 0
    ) || [];

  const selectedSize =
    size?.size ||
    (availableSizes.length === 1 ? (availableSizes[0]?.size as string) : "");

  const hasLightningDealEnd =
    (promotionContextState.hasPromotionEnded &&
      promoData &&
      promoData.promotion.promo_display_type ===
        PromotionDisplayType.LightningDeal) ||
    false;

  const { increaseCartItem, loading, error } = useIncreaseCartItem({
    onCompleted: (cartData) => {
      if (product) {
        const allCurrentCartItem = cartData?.updateMyCart?.lineItems.filter(
          ({ variant }) =>
            getCTAttribute("woo_product_id", variant?.attributesRaw) ===
            product?.id
        );
        const currentCartItem = allCurrentCartItem?.at(-1);
        const sizeValue = getCTAttribute(
          "size_value",
          currentCartItem?.variant?.attributesRaw
        ) as string;

        const selectedSizeSchema = getCTAttribute(
          "size_schema_code",
          currentCartItem?.variant?.attributesRaw
        ) as string;
        const convertedSize = getCTAttribute(
          "c_size",
          currentCartItem?.variant?.attributesRaw
        ) as string;

        setAddedProdToCart({
          productName: product.name,
          brandName: product.brand.name,
          price: product.price,
          regularPrice: product.regular_price,
          image: product.images?.[0] as string,
          selectedSize: selectedSize || sizeValue,
          selectedSizeSchema: selectedSizeSchema || undefined,
          convertedSize: convertedSize || undefined,
          promoCode: !hasLightningDealEnd
            ? (promoData?.promotion.promo_code as string) || undefined
            : undefined,
          promotionDisplayType: !hasLightningDealEnd
            ? (promoData?.promotion
                .promo_display_type as PromotionDisplayType) || undefined
            : undefined,
        });
      }
    },
  });

  const productGenderId =
    currentGenderId ||
    (product && product.gender && localeFix({ gender: product.gender }));

  const {
    segmentProductOutOfStock,
    segmentProductSizeSelected,
    segmentProductViewed,
    segmentProductEngaged,
    segmentBrandInfoOpened,
  } = useSegment();

  useEffect(() => {
    if (productGenderId) {
      dispatch(setGender(productGenderId));
    } else {
      dispatch(setGender("women"));
    }
  }, [productGenderId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (product?.id) {
      timerID.current = window.setTimeout(() => {
        void segmentProductEngaged(product);
      }, SEGMENT_ENGAGEMENT_TIMEOUT * 1000);
    }

    return () => {
      clearTimeout(timerID.current);
    };
  }, [product, segmentProductEngaged]);

  const isProductViewedTrackedRef = useRef("");

  useEffect(() => {
    if (!product || !product.id || isSessionLoading) {
      return;
    }

    if (
      pageViewTracked &&
      (!isProductViewedTrackedRef.current ||
        isProductViewedTrackedRef.current !== product.id)
    ) {
      void segmentProductViewed(
        product,
        queryID,
        objectID,
        algoliaIndex,
        isLoggedIn
      );

      isProductViewedTrackedRef.current = product.id;

      return;
    }
  }, [
    product,
    isLoggedIn,
    queryID,
    objectID,
    algoliaIndex,
    isSessionLoading,
    pageViewTracked,
    pageViewedDispatch,
    segmentProductViewed,
  ]);

  const prevRoute = useRef("");

  useEffect(() => {
    setSize(null);

    if (
      product?.id &&
      session?.user.email &&
      pageViewTracked &&
      prevRoute.current !== router.asPath
    ) {
      prevRoute.current = router.asPath;
      googleTagManager.trackProductPage(session?.user.email, product);
    }
  }, [product, pageViewTracked, session?.user.email, router.asPath]);

  const handleOpenSizeGuide = useCallback(() => setIsOpenSizeGuide(true), []);
  const handleCloseSizeGuide = useCallback(() => setIsOpenSizeGuide(false), []);

  const [isOpenBrandInfo, setIsOpenBrandInfo] = useState(false);
  const handleOpenBrandInfo = useCallback(() => {
    void segmentBrandInfoOpened({
      pageType: "PDP",
      brandName: product?.brand.name,
    });
    setIsOpenBrandInfo(true);
  }, [segmentBrandInfoOpened, product?.brand.name]);
  const handleCloseBrandInfo = useCallback(() => setIsOpenBrandInfo(false), []);

  const [addToCartError, setAddToCartError] = useState<boolean | undefined>();

  const handleCloseAddToCartError = useCallback(
    () => setAddToCartError(undefined),
    []
  );

  useEffect(() => {
    if (!!addToCartError && product?.id) {
      void segmentProductOutOfStock(product, selectedSize);
    }
  }, [addToCartError, product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (error) {
      setAddToCartError(true);
    }
  }, [setAddToCartError, error]);

  const handleAddToCart = useCallback(
    async (
      variationId: string,
      callback: (data?: UpdateCartData, error?: unknown) => void
    ) => {
      if (!data.cart || isCartLoading) {
        return;
      }

      const item = data.cart.lineItems.find(
        (lineItem) => lineItem?.variant?.sku === variationId
      );
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const currentQuantity = item?.quantity || 0;
      const availableQuantity =
        item?.variant?.availability?.channels?.results.reduce(
          (sum, stockInfo) =>
            sum + Number(stockInfo.availability.availableQuantity),
          0
        );

      try {
        if (
          availableQuantity &&
          currentQuantity &&
          availableQuantity <= currentQuantity
        ) {
          setAddToCartError(true);
        } else {
          selectedVariationId.current = variationId;

          const result = await increaseCartItem(
            variationId,
            item,
            getCartInfo(data.cart)
          );

          if (result?.data) {
            callback(result?.data);
          }
        }
      } catch (e) {
        callback(undefined, e);
      }
    },
    [increaseCartItem, data.cart, isCartLoading]
  );

  useEffect(() => {
    if (availableSizes.length === 1 && product) {
      void segmentProductSizeSelected(product, selectedSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (product && size) {
      void segmentProductSizeSelected(product, selectedSize);
    }
  }, [size]); // eslint-disable-line react-hooks/exhaustive-deps

  const productName = product?.name || "";
  const brandName = product?.brand?.name || "";
  const productImage = product?.images ? `${product.images[0]}` : "";

  // [TODO:Fix a bug with error handling and withApollo setup]
  // not only the page re-render due to mutation of the pageProps
  // but also the hook returns an error mutating from error to undefined
  // causing the page the render without data after multiple re-renders
  if (!productLoading && (!product || productError)) {
    return <Error />;
  }

  const desciption = t(
    i18n
  )`Shop ${productName} and other items from ${brandName} at Otrium with discounts up to 70%! Check out this item and other deals for great prices!`;

  const ribbonMessage = t(i18n)`Lightning Deal ends in`;

  const uppercaseBrandName = product?.brand?.name?.toUpperCase() || "";
  const noIndexationBrands = ["GANNI", "SUITSUPPLY"];

  const shouldIndex = !noIndexationBrands.includes(uppercaseBrandName);

  const openLoginRegisterModal = (
    actionSource: string,
    variationId?: string
  ) => {
    setActionSource(actionSource);
    setIsLoginRegisterModalOpen(true);
    setAddToCartVariationId(variationId);
  };

  return (
    <StyledProductView>
      <PageHead
        title={t(i18n)`${
          product?.name ? `${product.name} | ` : ""
        }Online Outlet | Otrium`}
        deeplink={`otrium://product/${slug}`}
        description={desciption}
        isPDP={shouldIndex}
        metaTags={
          <>
            <meta name="product_id" content={product?.id} />
            {/* Twitter */}
            <meta
              name="twitter:title"
              content={`${productName} | ${brandName} | Otrium`}
            />
            <meta name="twitter:card" content="product" />
            <meta name="twitter:image" content={productImage} />
            <meta
              name="twitter:data1"
              content={product?.price ? `${product.price} ${currency}` : ""}
            />
            <meta name="twitter:label1" content={t(i18n)`Price`} />
            {/* Open Graph / Facebook */}
            <meta
              property="og:title"
              content={`${productName} | ${brandName} | Otrium`}
            />
            <meta property="og:url" content={`otrium://product/${slug}`} />
            <meta name="og:image" content={productImage} />
            <meta property="og:site_name" content="Otrium Shop"></meta>
            <meta property="og:type" content="product" />
          </>
        }
      />
      <ProductContent as="main">
        <div className="product-details">
          {productGenderId ? (
            <div className="product-breadcrumb-wrapper">
              <ProductBreadcrumbs
                product={product}
                genderId={productGenderId}
                locale={locale}
              />
            </div>
          ) : null}

          {product ? (
            <ImageCarouselContextProvider>
              <ProductPhotos
                key={product.id}
                photos={product.images || []}
                labels={product.labels || []}
                productName={product.name}
                productId={product.id}
              />
            </ImageCarouselContextProvider>
          ) : (
            <Skeleton />
          )}

          {product ? (
            <div className="ProductView__productSidebar">
              <ProductInfo
                labels={product.labels || []}
                englishGenderId={productGenderId as ProductGender}
                productName={product.name}
                brandName={product.brand.name}
                brandSlug={product.brand.slug}
                price={product.price}
                regularPrice={product.regular_price}
                lowestPrice={product.lowest_price}
                promoCode={
                  promoData?.promotion.promo_display_type ===
                  PromotionDisplayType.LightningDeal
                    ? (promoData.promotion.promo_code as string)
                    : undefined
                }
              />

              {product && !productError && promoData && promoData.promotion ? (
                <>
                  {promoData.promotion.promo_display_type ===
                  PromotionDisplayType.LightningDeal ? (
                    <PromotionalRibbon
                      icon={<Flash />}
                      message={ribbonMessage}
                      promotion={promoData.promotion}
                    />
                  ) : null}
                  {promoData.promotion.promo_display_type ===
                  PromotionDisplayType.FlashSale ? (
                    <PromoTimer
                      promotion={promoData.promotion}
                      margins={{
                        marginBlockEnd: "space0",
                        marginBlockStart: "space0",
                      }}
                    />
                  ) : null}
                  <PromotionalBlock promotion={promoData.promotion} />
                </>
              ) : null}
              <ProductOrder
                key={product.slug}
                className="ProductView__productOrder"
                product={product}
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onAddToCart={handleAddToCart}
                addToCartLoading={loading || isCartLoading}
                handleSizeChange={setSize}
                hasSizeGuide={true}
                hasAddToWishlistButton
                onOpenSizeGuide={handleOpenSizeGuide}
                sizeGuideRef={sizeGuideRef}
                queryID={queryID}
                objectID={objectID}
                algoliaIndex={algoliaIndex}
                withMobileOverlay
                customerType={
                  data?.cart?.id
                    ? "existing"
                    : data?.cart === null
                    ? "new"
                    : "guest"
                }
                openLoginRegisterModal={openLoginRegisterModal}
              />
              <ProductMeta
                key={product.id}
                className="ProductView__productMeta"
                data-testid="product-view-meta"
                onOpenBrandInfo={handleOpenBrandInfo}
                locale={locale}
                description={product.description}
                material={product.material as string}
                brand={product.brand}
                shippingText={product?.shipping?.shipping_text}
                paymentTypes={product?.payment_types}
                fitSize={product.fit_size}
                careInstructions={product.care_instructions}
                goodOnYouInfo={product.brand.good_on_you_info}
                openLoginRegisterModal={openLoginRegisterModal}
              />
            </div>
          ) : (
            <ProductSidebarSkeleton className="ProductView__productSidebar" />
          )}
          {product && (
            <AddToCollection productsIds={[parseInt(product.id, 10)]} />
          )}
        </div>
      </ProductContent>
      {product && (
        <ProductViewCarousels
          slug={product.slug}
          shopType={(currentShopType as ProductGender) ?? "women"}
        />
      )}
      <NoSSR>
        <Content>
          {product && (
            <>
              <Modal isOpen={isOpenSizeGuide} onClose={handleCloseSizeGuide}>
                <SizeGuide
                  productGenderId={productGenderId as ProductGender}
                  brandSlug={product.brand.slug}
                  onClose={handleCloseSizeGuide}
                  ref={sizeGuideRef}
                />
              </Modal>
              <Modal
                isOpen={!!addToCartError}
                withHeader={true}
                onClose={handleCloseAddToCartError}
              >
                <OutOfStock
                  genderId={productGenderId as ProductGender}
                  brandSlug={product.brand.slug}
                  onClose={handleCloseAddToCartError}
                />
              </Modal>
              {brand ? (
                <AlgoliaBrandInfo
                  open={isOpenBrandInfo}
                  onClose={handleCloseBrandInfo}
                  brand={brand}
                />
              ) : null}
            </>
          )}
        </Content>
      </NoSSR>
      {isLoginRegisterModalOpen && (
        <LoginRegisterModal
          open={isLoginRegisterModalOpen}
          onClose={() => {
            setIsLoginRegisterModalOpen(false);
          }}
          redirectTo={path}
          trackingData={{
            pageName: GATED_PRODUCT_PAGE,
            shopType: localeGenderId as ShopType,
            actionSource,
            productName: product?.name,
            productId: product?.id,
            level: product?.gate?.level || "undefined",
            redirectTo: path,
          }}
          onRedirect={(type) => {
            removeStorageItem(GATED_POPUP_REDIRECT_DATA);
            setStorageJSON(GATED_POPUP_REDIRECT_DATA, {
              gatedActionType: type,
              addToCartData: {
                variationId: addToCartVariationId,
                productId: product?.id,
                product,
                queryID,
                objectID,
                algoliaIndex,
              },
              actionSource,
            });
          }}
        />
      )}
    </StyledProductView>
  );
};

export default ProductView;
